import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react'
import React, { Children, useLayoutEffect, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'

import { SCROLL_AMOUNT } from './navbar-menu.constants'
import * as S from './navbar-menu.styled'
import { NavbarMenuProps } from './navbar-menu.types'

export const NavbarMenu = ({
  className,
  children,
  disableChildrenWrap = false,
  mode = 'default',
}: NavbarMenuProps) => {
  const listRef = useRef<HTMLUListElement>(null)
  const [hasOverflow, setHasOverflow] = useState(false)
  const isPointerDownRef = useRef(false)
  const startXRef = useRef(0)
  const startScrollXRef = useRef(0)

  const checkForOverflow = () => {
    const el = listRef.current
    if (!el || mode === 'tabs') return
    const totalChildrenWidth = Array.from(el.children).reduce(
      (sum, child) => sum + child.getBoundingClientRect().width,
      0,
    )
    setHasOverflow(totalChildrenWidth > el.getBoundingClientRect().width)
  }

  useLayoutEffect(() => {
    const el = listRef.current
    if (!el) return

    const mutationObserver = new MutationObserver(checkForOverflow)
    mutationObserver.observe(el, { childList: true, subtree: true })
    return () => mutationObserver.disconnect()
  }, [])

  useLayoutEffect(() => {
    const el = listRef.current
    if (!el) return

    const resizeObserver = new ResizeObserver(checkForOverflow)
    resizeObserver.observe(el)
    return () => resizeObserver.disconnect()
  }, [])

  const handleScrollLeft = () =>
    listRef.current?.scrollBy({ behavior: 'smooth', left: -SCROLL_AMOUNT })
  const handleScrollRight = () =>
    listRef.current?.scrollBy({ behavior: 'smooth', left: SCROLL_AMOUNT })

  const handlePointerDown = (e: React.PointerEvent<HTMLUListElement>) => {
    isPointerDownRef.current = true
    startXRef.current = e.clientX
    startScrollXRef.current = e.currentTarget.scrollLeft
  }

  const handlePointerMove = (e: React.PointerEvent<HTMLUListElement>) => {
    if (!isPointerDownRef.current) return
    const dx = e.clientX - startXRef.current
    if (Math.abs(dx) < 1) return
    e.currentTarget.scrollLeft = startScrollXRef.current - dx
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  const handlePointerUpOrCancel = (e: React.PointerEvent<HTMLUListElement>) => {
    isPointerDownRef.current = false
    e.currentTarget.releasePointerCapture(e.pointerId)
  }

  return (
    <S.ScrollContainer className={className} data-component-id="NavbarMenu">
      {hasOverflow && (
        <Button onlyBox use="ghost" size="small" onClick={handleScrollLeft}>
          <Icon icon={CaretLeftIcon} />
        </Button>
      )}
      <S.ScrollableNav>
        <S.ScrollableNavList
          ref={listRef}
          $mode={mode}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUpOrCancel}
          onPointerCancel={handlePointerUpOrCancel}
        >
          {disableChildrenWrap && children}
          {!disableChildrenWrap &&
            Children.map(children, (node) => {
              if (!node) return
              const key = typeof node === 'object' && 'key' in node ? node.key : undefined
              return <li key={key}>{node}</li>
            })}
        </S.ScrollableNavList>
      </S.ScrollableNav>
      {hasOverflow && (
        <Button onlyBox use="ghost" size="small" onClick={handleScrollRight}>
          <Icon icon={CaretRightIcon} />
        </Button>
      )}
    </S.ScrollContainer>
  )
}
